import { getProfileDataThunk, resetUserEmailOrPhoneThunk } from '../../../../store/workspace/thunks';
import { xhrDefaultMessage } from '../../../../configs/notification';
import { useDispatch } from '../../../../store/store';
import { useEffect, useState } from 'react';
import { CredentialResetStep } from '../types';

/** Length of the verification code, which auto-submits once complete. */
const OTP_LENGTH = 6;

/**
 * Drives the two step verification shared by the "edit email" and "edit phone
 * number" modals: request a code, confirm it, then refresh the profile.
 *
 * @param buildPayload Identity fields of the request, or `null` while the form
 * is not ready to be sent. It is read on submit, never during render.
 */
export function useCredentialReset(buildPayload: () => Record<string, unknown> | null) {
    const dispatch = useDispatch();
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [responseError, setResponseError] = useState('');
    const [step, setStep] = useState<CredentialResetStep>('Request');

    const send = (verificationCode?: string) => {
        const payload = buildPayload();
        if (!payload) return;

        setIsLoading(true);
        dispatch(resetUserEmailOrPhoneThunk(
            verificationCode ? { ...payload, verificationCode } : payload,
        ))
            .unwrap()
            .then(() => {
                if (!verificationCode) {
                    setStep('Confirm');
                    return;
                }

                setStep('Done');
                dispatch(getProfileDataThunk());
            })
            .catch((error) => {
                setResponseError(error?.message || xhrDefaultMessage);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const requestCode = () => send();
    const confirmCode = () => send(otp);

    useEffect(() => {
        if (otp.length !== OTP_LENGTH) return;
        confirmCode();
    }, [otp]);

    return {
        otp,
        step,
        setOtp,
        setStep,
        isLoading,
        confirmCode,
        requestCode,
        responseError,
    };
};
